import { CianChatButtonWithAnimation } from '@cian/newbuilding-chat-component';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { onAnimationShown, onAnimationTooltipShown } from './analytics/analytics';
import { useChatAnimationCounter } from './hooks/useChatAnimationCounter';
import { getTooltipAnimationCookie, setTooltipAnimationCookie } from './utils/chatAnimationCounter';
import { isChatExistSelector, isLoadingSelector } from '../../selectors/consultantCianChat';
import { getFirstGaLabel } from '../../selectors/analytics';

export const CianChatButtonWithAnimationContainer = ({ onClick }: { onClick(): void }) => {
  const trackingCustomLabel = useSelector(getFirstGaLabel);
  const isLoading = useSelector(isLoadingSelector);
  const isChatExist = useSelector(isChatExistSelector);

  const { incrementAnimationShownCounter, shouldShowAnimation, animationDelayMs } = useChatAnimationCounter();

  const [isTooltipAnimationVisible, setIsTooltipAnimationVisible] = React.useState(() => {
    const tooltipCookieExists = getTooltipAnimationCookie();

    return !tooltipCookieExists && shouldShowAnimation && !isChatExist;
  });

  const isTooltipAnimationVisibleRef = React.useRef(isTooltipAnimationVisible);
  const [isMounted, setIsMounted] = React.useState(false);

  React.useEffect(() => {
    setIsMounted(true);
  }, []);

  React.useEffect(() => {
    const tooltipCookieExists = getTooltipAnimationCookie();
    // istanbul ignore if
    if (tooltipCookieExists) {
      setIsTooltipAnimationVisible(false);
      isTooltipAnimationVisibleRef.current = false;
    } else {
      const newValue = shouldShowAnimation && !isChatExist;
      setIsTooltipAnimationVisible(newValue);
      isTooltipAnimationVisibleRef.current = newValue;
    }
  }, [shouldShowAnimation, isChatExist]);

  // istanbul ignore next
  const handleAnimationStarted = React.useCallback(() => {
    onAnimationShown({ label: trackingCustomLabel });
    if (isTooltipAnimationVisibleRef.current) {
      onAnimationTooltipShown({ label: trackingCustomLabel });
    }

    setTooltipAnimationCookie();
    setTimeout(incrementAnimationShownCounter, 7000);
  }, [trackingCustomLabel, incrementAnimationShownCounter]);

  if (!isMounted) {
    return null;
  }

  return (
    <CianChatButtonWithAnimation
      data-testid="ConsultantChatButton"
      isLoading={isLoading}
      isButtonAnimationVisible={shouldShowAnimation}
      isTooltipAnimationVisible={isTooltipAnimationVisible}
      onClick={onClick}
      animationDelayMs={animationDelayMs}
      onAnimationStarted={handleAnimationStarted}
    />
  );
};
